import {
  ALCOHOL,
  AROMA_INTENSITY,
  BODY,
  CLARITY,
  DEVELOPMENT,
  FINISH,
  FIVE_STEP,
  MOUSSE,
  NOSE_CONDITION,
  QUALITY,
  READINESS,
  RED_HUES,
  ROSE_HUES,
  SWEETNESS,
  VISUAL_INTENSITY,
  WHITE_HUES,
  WINE_COLOUR,
} from "@proef/domain";
import { z } from "zod";
import { FlightJoinCode, FlightStatus } from "./flight.js";
import { WineIdentity } from "./wine.js";

/** An aroma id the lexicon names, or a family; the edge only checks the shape, the app the meaning. */
const DescriptorId = z.string().min(1).max(80);

/**
 * The note a taster is still writing for one wine, field for field what the app keeps while they
 * answer. It is never a `StructuredNote`: a verdict may be missing and nothing is cross-checked.
 */
export const TastingRunDraft = z.object({
  depth: z.enum(["quick", "deep"]),
  sparkling: z.boolean(),
  clarity: z.enum(CLARITY),
  intensity: z.enum(VISUAL_INTENSITY),
  colour: z.enum(WINE_COLOUR),
  hue: z.enum([...WHITE_HUES, ...ROSE_HUES, ...RED_HUES]),
  appearanceOther: z.string().max(280),
  noseCondition: z.enum(NOSE_CONDITION),
  noseIntensity: z.enum(AROMA_INTENSITY),
  noseDescriptors: z.array(DescriptorId).max(40),
  development: z.enum(DEVELOPMENT),
  sweetness: z.enum(SWEETNESS),
  acidity: z.enum(FIVE_STEP),
  tannin: z.enum(FIVE_STEP).optional(),
  alcohol: z.enum(ALCOHOL),
  body: z.enum(BODY),
  mousse: z.enum(MOUSSE).optional(),
  flavourIntensity: z.enum(FIVE_STEP),
  palateDescriptors: z.array(DescriptorId).max(40),
  finish: z.enum(FINISH),
  quality: z.enum(QUALITY).optional(),
  readiness: z.enum(READINESS),
  freeText: z.string().max(4000),
});
export type TastingRunDraft = z.infer<typeof TastingRunDraft>;

export const TastingRunWine = z.object({
  key: z.string().min(1).max(64),
  wine: WineIdentity.optional(),
  blind: z.boolean(),
  flightWineId: z.string().min(1).max(64).optional(),
  tastingId: z.string().min(1).max(64).optional(),
  /** A saved note reopened for editing: the journal entry it replaces once the edit is saved. */
  replaces: z.string().min(1).max(64).optional(),
  /** The day the replaced note was tasted, kept when the edit is saved. */
  tastedAt: z.iso.datetime().optional(),
  saved: z.boolean(),
  queued: z.boolean(),
  draft: TastingRunDraft,
  guess: z.object({ grape: z.string().max(80), country: z.string().max(2) }),
  step: z.number().int().min(0).max(8),
  /** The line for the group typed on the summary (at most 140 characters; absent in older copies). */
  quote: z.string().max(560).default(""),
});
export type TastingRunWine = z.infer<typeof TastingRunWine>;

/**
 * A tasting in progress as the app holds it: the table of wines, which one is in front of the
 * taster, and what each draft says so far. The same shape sits in the device's storage and, for a
 * person who opted in, encrypted at the edge, so a second device can pick the tasting up.
 */
export const TastingRunSnapshot = z.object({
  id: z.string().min(1).max(64),
  depth: z.enum(["quick", "deep"]),
  blind: z.boolean(),
  title: z.string().max(200),
  wines: z.array(TastingRunWine).max(60),
  current: z.number().int().min(0).max(59),
  started: z.boolean(),
  host: z.boolean(),
  finished: z.boolean(),
  /** Whether live picks are shared with the others in a shared tasting (absent in older copies). */
  sharePicks: z.boolean().default(true),
  flightCode: FlightJoinCode.optional(),
  sessionId: z.string().min(1).max(64).optional(),
  /** The account the tasting belongs to; absent while the taster was signed out. */
  ownerId: z.string().min(1).max(64).optional(),
  /** When this copy last changed, on the device that changed it: the newest copy wins. */
  updatedAt: z.iso.datetime(),
  /** When the first copy was made, for "started 20 minutes ago". */
  startedAt: z.iso.datetime().optional(),
});
export type TastingRunSnapshot = z.infer<typeof TastingRunSnapshot>;

/** `PUT /v1/tasting-drafts/:runId` -- one unfinished tasting, replacing the stored copy when newer. */
export const PutTastingDraftRequest = z.strictObject({
  snapshot: TastingRunSnapshot,
});
export type PutTastingDraftRequest = z.infer<typeof PutTastingDraftRequest>;

/** `accepted: false` says a newer copy is already stored (another device wrote it); `updatedAt` is its stamp. */
export const PutTastingDraftResponse = z.object({
  accepted: z.boolean(),
  updatedAt: z.iso.datetime(),
});
export type PutTastingDraftResponse = z.infer<typeof PutTastingDraftResponse>;

export const TastingDraftItem = z.object({
  runId: z.string().min(1).max(64),
  updatedAt: z.iso.datetime(),
  snapshot: TastingRunSnapshot,
});
export type TastingDraftItem = z.infer<typeof TastingDraftItem>;

/** `GET /v1/tasting-drafts` -- with the switch off the list is empty and says so. */
export const TastingDraftList = z.object({
  enabled: z.boolean(),
  items: z.array(TastingDraftItem),
});
export type TastingDraftList = z.infer<typeof TastingDraftList>;

/** `GET`/`PATCH /v1/tasting-drafts/settings` -- the opt-in. Off removes every stored draft. */
export const TastingDraftSettings = z.object({ enabled: z.boolean() });
export type TastingDraftSettings = z.infer<typeof TastingDraftSettings>;

export const PatchTastingDraftSettingsRequest = z.strictObject({ enabled: z.boolean() });
export type PatchTastingDraftSettingsRequest = z.infer<typeof PatchTastingDraftSettingsRequest>;

/** One flight the caller is in and that has not finished (`GET /v1/flights?mine=1`). */
export const FlightSummary = z.object({
  code: FlightJoinCode,
  title: z.string(),
  blind: z.boolean(),
  status: FlightStatus.exclude(["done"]),
  wineCount: z.number().int().nonnegative(),
  currentIndex: z.number().int().nonnegative(),
  isHost: z.boolean(),
  /** How many wines the caller has already sent a note for. */
  notesSubmitted: z.number().int().nonnegative(),
  /** The session around the flight, when it has one and the caller is in it. */
  sessionId: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type FlightSummary = z.infer<typeof FlightSummary>;

export const FlightList = z.object({ items: z.array(FlightSummary) });
export type FlightList = z.infer<typeof FlightList>;
